import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {BaseComponent} from '../../components/general/base/base.component';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  isAfter,
  setHours,
  setMinutes,
  subDays,
  subMonths,
  subWeeks,
  subYears
} from 'date-fns/esm';
import { forEach } from 'lodash-es';
import {
  dateFilterValues,
  ElementFilters,
  eventTypes,
  FilterChangeEvent,
  FilterDefinitionEntry,
  Filters,
  FilterValue,
  Property,
  ViewOptions,
  VivoBody
} from './element-filters';
import { VivoService } from '../../vivo/vivo.service';
import { Station } from '../../custom/vrgroup/common/station';
import { VRGroupService } from '../../custom/vrgroup/vrgroup.service';

@Component({
  selector: 'qm-element-filters',
  templateUrl: './element-filters.component.html',
  styleUrls: ['./element-filters.component.scss']
})
export class ElementFiltersComponent extends BaseComponent implements OnInit {
  public eventType = eventTypes;
  public dateFilters = dateFilterValues;

  public startDate: Date;
  public endDate: Date;

  public vivoOrder: {orderBy: string, reverse: boolean} = {orderBy: 'updatedAt', reverse: true};
  public selectedTask: {type: string, template_id: string} = {type: 'All', template_id: ''};

  public selectedFilters: ElementFilters = {
    date: 'All',
    text: '',
    type: 'All'
  };
  public vivoBody: VivoBody = new VivoBody();
  public vivoTasks: {name: string, value: string}[] = [];
  private allTasks: {_id: string}[] = [];
  public stations: Station[] = [];

  @Output() filtersChanged: EventEmitter<FilterChangeEvent> = new EventEmitter<FilterChangeEvent>();
  @Input() alwaysActive: boolean = false;
  @Input() rangeFilter: boolean = true;
  @Input() vivoFilters: boolean = false;
  @Input() typeFilter: boolean = true;
  @Input() filterDefinitions: FilterDefinitionEntry[];

  /**
   * Boolean for showing filters div
   *@type {boolean}
   */
  public activeFilters: boolean = false;

  constructor(private vivoService: VivoService) {
    super();
    this.name = 'ElementFiltersComponent';
    this.startDate = new Date();
    this.endDate = new Date();
    this.startDate.setHours(0, 0, 0, 0);
    this.endDate.setHours(23, 59, 59, 999);
  }

  ngOnInit() {
    if (this.alwaysActive) {
      this.activeFilters = true;
    }
    if (this.vivoFilters) {
      this.getVivoVariables();
    }
  }

  public selectedFilterChanged() {
    this.filtersChanged.emit(
      new FilterChangeEvent(this.startDate, this.endDate,
        this.selectedFilters, this.filterDefinitions, this.vivoBody)
    );
  }

  private async getVivoVariables() {
    const responses = await Promise.all([
      this.vivoService.vivoTaskType(),
      this.vivoService.getVivoUsers(),
      this.vivoService.vivoTaskPrioritys()
    ]);
    forEach(responses[0].docs, (o: any) => {
      if (o.id !== this.vivoService.getDiaryTaskType()) {
        this.vivoTasks.push({name: o.name, value: o.id});
        this.allTasks.push({_id: o.id});
      }
    });
  }

  public vivoTask() {
    this.vivoBody.filters = [];
    let value: {_id: string}[];
    const property: Property = {
      _id: 'taskType',
      type: 'taskType'
    };
    if (this.selectedTask.type === 'All') {
      value = this.allTasks;
    } else {
      value = [{_id: this.selectedTask.template_id}];
    }
    this.vivoBody.filters.push({property, value});
    this.vivoOrderBy();
  }

  public vivoOrderBy() {
    this.vivoBody.viewOptions = [];
    const view: ViewOptions = {
      orderBy: this.vivoOrder.orderBy,
      reverse: this.vivoOrder.reverse
    };
    this.vivoBody.viewOptions.push(view);
    this.selectedFilterChanged();
  }

  public vivoDate() {
    const temp = this.vivoBody.filters.filter(o => {
      return !o['option'];
    });
    const before: Filters = {
      option: {
        value: 'before'
      },
      query: [{
        key: 'updatedAt',
        value: this.endDate.toISOString()
      }],
      value: this.endDate.toISOString()
    };
    const after: Filters = {
      option: {
        value: 'after'
      },
      query: [{
        key: 'updatedAt',
        value: this.startDate.toISOString()
      }],
      value: this.startDate.toISOString()
    };
    this.vivoBody.filters = [];
    this.vivoBody.filters.push(temp[0]);
    this.vivoBody.filters.push(before, after);
    if (this.vivoBody.viewOptions.length < 0) {
      this.vivoOrderBy();
    } else {
      this.selectedFilterChanged();
    }
  }

  public timeTravels(direction: 'next' | 'previous' | ''): void {
    if (direction === 'next') {
      this.timeTravelsNext();
    } else if (direction === 'previous') {
      this.timeTravelsPrevious();
    } else {
      this.timeTravelsNone();
    }
  }

  private timeTravelsNext(): void {
    this.startDate = new Date(this.endDate.getTime());
    switch (this.selectedFilters.date) {
      case 'Day':
        this.endDate = addDays(this.startDate, 1);
        break;
      case 'Week':
        this.endDate = addWeeks(this.startDate, 1);
        break;
      case 'Month':
        this.endDate = addMonths(this.startDate, 1);
        break;
      case 'Year':
        this.endDate = addYears(this.startDate, 1);
        break;
    }
    if (this.futureCheck()) {
      if (this.vivoFilters) {
        this.vivoDate();
      } else {
        this.selectedFilterChanged();
      }
    }
  }

  private timeTravelsPrevious(): void {
    this.endDate = new Date(this.startDate);
    switch (this.selectedFilters.date) {
      case 'Day':
        this.startDate = subDays(this.endDate, 1);
        break;
      case 'Week':
        this.startDate = subWeeks(this.endDate, 1);
        break;
      case 'Month':
        this.startDate = subMonths(this.endDate, 1);
        break;
      case 'Year':
        this.startDate = subYears(this.endDate, 1);
        break;
    }
    if (this.vivoFilters) {
      this.vivoDate();
    } else {
      this.selectedFilterChanged();
    }
  }

  private timeTravelsNone(): void {
    switch (this.selectedFilters.date) {
      case 'Day':
        this.startDate = subDays(this.endDate, 1);
        break;
      case 'Week':
        this.startDate = subWeeks(this.endDate, 1);
        break;
      case 'Month':
        this.startDate = subMonths(this.endDate, 1);
        break;
      case 'Year':
        this.startDate = subYears(this.endDate, 1);
        break;
    }
    if (this.vivoFilters) {
      this.vivoDate();
    } else {
      this.selectedFilterChanged();
    }
  }

  private futureCheck(): boolean {
    const today = new Date();
    if (isAfter(this.endDate, today)) {
      this.startDate = new Date(today.getTime());
      this.timeTravelsPrevious();
      return false;
    }
    return true;
  }

  public getActiveFilterCount(filters: FilterValue[]): string {
    if (filters) {
      const l = filters.filter(o => {
        return o.active;
      }).length;
      return '(' + l + '/' + filters.length + ')';
    }
  }
}
